import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronRight, ChevronLeft, Check, Store, Settings,
  Link as LinkIcon, Zap, PartyPopper, ArrowLeft, Loader, AlertCircle
} from 'lucide-react';

const PartnerSetupWizard = ({ setPage }) => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';

  const [currentStep, setCurrentStep] = useState(1);
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [retailerConfig, setRetailerConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    vendorNumber: '',
    sftpUsername: '',
    sftpPassword: '',
    sftpHost: '',
    sftpPort: 22,
    inboundPath: '/inbound',
    outboundPath: '/outbound'
  });

  const totalSteps = 5;

  useEffect(() => {
    fetchRetailers();
  }, []);

  const FALLBACK_RETAILERS = [
    { id: 'walmart',  name: 'Walmart',  ediIdentifier: '0009291264', yourEdiId: '', sftpHost: 'sftp.walmart.com',  sftpPort: 22, inboundPath: '/inbound',  outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'target',   name: 'Target',   ediIdentifier: '0045000012', yourEdiId: '', sftpHost: 'sftp.target.com',   sftpPort: 22, inboundPath: '/inbound',  outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'kohls',    name: "Kohl's",   ediIdentifier: '0070170776', yourEdiId: '', sftpHost: 'sftp.kohls.com',    sftpPort: 22, inboundPath: '/inbound',  outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'amazon',   name: 'Amazon',   ediIdentifier: '0014702322', yourEdiId: '', sftpHost: 'sftp.amazon.com',   sftpPort: 22, inboundPath: '/inbound',  outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'costco',   name: 'Costco',   ediIdentifier: '0047863267', yourEdiId: '', sftpHost: 'sftp.costco.com',   sftpPort: 22, inboundPath: '/inbound',  outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'dicks',    name: "Dick's Sporting Goods", ediIdentifier: '0012463999', yourEdiId: '', sftpHost: 'sftp.dickssportinggoods.com', sftpPort: 22, inboundPath: '/inbound', outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'nordstrom',name: 'Nordstrom', ediIdentifier: '0011956781', yourEdiId: '', sftpHost: 'sftp.nordstrom.com', sftpPort: 22, inboundPath: '/inbound', outboundPath: '/outbound', connectionType: 'sftp' },
    { id: 'macys',    name: "Macy's",   ediIdentifier: '0013598401', yourEdiId: '', sftpHost: 'sftp.macys.com',    sftpPort: 22, inboundPath: '/inbound',  outboundPath: '/outbound', connectionType: 'sftp' },
  ];

  const fetchRetailers = async () => {
    try {
      const response = await fetch(API_URL + '/api/retailers', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        setRetailers(FALLBACK_RETAILERS);
      } else {
        setRetailers(data);
      }
      setLoading(false);
    } catch (err) {
      // Fall back to hardcoded retailers so the UI never breaks
      setRetailers(FALLBACK_RETAILERS);
      setLoading(false);
    }
  };

  const selectRetailer = async (retailer) => {
    setSelectedRetailer(retailer);
    try {
      const response = await fetch(API_URL + '/api/retailers/' + retailer.id, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const config = await response.json();
      setRetailerConfig(config);
      setFormData(prev => ({
        ...prev,
        sftpHost: config.sftp.host,
        sftpPort: config.sftp.port,
        inboundPath: config.sftp.inboundPath,
        outboundPath: config.sftp.outboundPath
      }));
    } catch (err) {
      console.error('Failed to load retailer config:', err);
    }
  };

  const handleSavePartner = async () => {
    setSaving(true);
    try {
      const response = await fetch(API_URL + '/api/partners', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({
          name: selectedRetailer.name,
          ediIdentifier: retailerConfig?.edi?.identifier || "",
          yourEdiId: formData.vendorNumber,
          sftpHost: formData.sftpHost,
          sftpPort: formData.sftpPort,
          sftpUsername: formData.sftpUsername,
          sftpPassword: formData.sftpPassword,
          inboundPath: formData.inboundPath,
          outboundPath: formData.outboundPath,
          connectionType: retailerConfig?.sftp?.protocol || "SFTP".toLowerCase(),
          status: 'active'
        })
      });
      if (response.ok) {
        setCurrentStep(5);
      } else {
        setError('Failed to save partner. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setSaving(false);
  };

  const nextStep = () => {
    if (currentStep === 4) {
      handleSavePartner();
    } else if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const inputStyle = {
    width: '100%', padding: '16px', borderRadius: '12px', border: '2px solid #e2e8f0',
    fontSize: '15px', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' };
  const fieldGroup = { marginBottom: '24px' };

  const ProgressBar = () => (
    <div style={{ marginBottom: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        {[1,2,3,4,5].map(step => (
          <div key={step} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: step <= currentStep ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#e2e8f0',
              color: step <= currentStep ? 'white' : '#94a3b8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', fontWeight: '800',
              boxShadow: step === currentStep ? '0 0 0 4px rgba(16, 185, 129, 0.2)' : 'none',
              position: 'relative', zIndex: 2
            }}>
              {step < currentStep ? <Check size={24} strokeWidth={3} /> : step}
            </div>
            {step < 5 && (
              <div style={{
                flex: 1, height: '4px',
                background: step < currentStep ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' : '#e2e8f0',
                marginLeft: '-12px', marginRight: '-12px', zIndex: 1
              }} />
            )}
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'center', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
        Step {currentStep} of {totalSteps}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc 0%, #e2e8f0 100%)' }}>
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white', padding: '48px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
          <button onClick={() => setPage('dashboard')} style={{
            backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)',
            color: 'white', padding: '12px 24px', borderRadius: '12px', fontSize: '15px',
            fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px'
          }}>
            <ArrowLeft size={18} /> Back to Dashboard
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '16px', padding: '12px' }}>
              <Store size={32} strokeWidth={2.5} />
            </div>
            <div>
              <h1 style={{ fontSize: '42px', fontWeight: '900', marginBottom: '8px', letterSpacing: '-2px' }}>Add Trading Partner</h1>
              <p style={{ fontSize: '18px', opacity: 0.95 }}>Connect to a major retailer in just 5 simple steps</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px', marginTop: '-40px' }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '24px', padding: '48px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)', border: '1px solid rgba(226,232,240,0.8)'
        }}>
          <ProgressBar />

          {error && (
            <div style={{
              padding: '12px 16px', borderRadius: '8px', marginBottom: '24px',
              backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px'
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {/* Step 1: Choose Retailer */}
          {currentStep === 1 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginBottom: '12px' }}>Choose Your Retail Partner</h2>
                <p style={{ fontSize: '16px', color: '#64748b' }}>Select the retailer you want to connect with — {retailers.length} retailers supported</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                {retailers.map(retailer => {
                  const isSelected = selectedRetailer?.id === retailer.id;
                  return (
                    <div key={retailer.id} onClick={() => selectRetailer(retailer)} style={{
                      padding: '24px', borderRadius: '16px',
                      border: isSelected ? '3px solid #10b981' : '3px solid #e2e8f0',
                      backgroundColor: isSelected ? 'rgba(16,185,129,0.05)' : 'white',
                      cursor: 'pointer', transition: 'all 0.2s', position: 'relative'
                    }}>
                      {isSelected && (
                        <div style={{
                          position: 'absolute', top: '12px', right: '12px', width: '32px', height: '32px',
                          borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <Check size={18} style={{ color: 'white' }} strokeWidth={3} />
                        </div>
                      )}
                      <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>{retailer.name}</div>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>{retailer.description}</div>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', backgroundColor: '#f1f5f9', color: '#475569' }}>
                          {retailer.documentsSupported} documents
                        </span>
                        <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', backgroundColor: '#f1f5f9', color: '#475569' }}>
                          {retailer.protocol}
                        </span>
                        <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', backgroundColor: '#f1f5f9', color: '#475569' }}>
                          ~{retailer.estimatedOnboardingDays} days onboarding
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div onClick={() => { setSelectedRetailer({ id:"custom", name:"Custom Partner" }); setRetailerConfig(null); }} style={{ padding: "24px", borderRadius: "16px", border: selectedRetailer?.id==="custom" ? "3px solid #10b981" : "3px dashed #94a3b8", backgroundColor: selectedRetailer?.id==="custom" ? "rgba(16,185,129,0.05)" : "white", cursor: "pointer", transition: "all 0.2s", textAlign: "center" }}>
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>➕</div>
                  <div style={{ fontSize: "24px", fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>Other / Custom</div>
                  <div style={{ fontSize: "14px", color: "#64748b" }}>Add any retailer not listed above</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: EDI Configuration */}
          {currentStep === 2 && (selectedRetailer?.id === "custom" || retailerConfig) && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginBottom: '12px' }}>EDI Configuration</h2>
                <p style={{ fontSize: '16px', color: '#64748b' }}>Auto-configured for {selectedRetailer.name} — review the settings below</p>
              </div>
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Retailer</label>
                  <input type="text" value={selectedRetailer.name} readOnly style={{ ...inputStyle, backgroundColor: '#f8fafc', color: '#64748b', fontWeight: '600' }} />
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Retailer EDI Identifier</label>
                  <input type="text" value={retailerConfig?.edi?.identifier || ""} readOnly style={{ ...inputStyle, backgroundColor: '#f8fafc', color: '#64748b', fontFamily: 'monospace' }} />
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Your Vendor Number *</label>
                  <input type="text" value={formData.vendorNumber} onChange={(e) => setFormData({...formData, vendorNumber: e.target.value})}
                    placeholder="Enter your vendor number from the retailer" style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#10b981'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>This is assigned by {selectedRetailer.name} when you become an approved vendor</span>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>EDI Version</label>
                  <input type="text" value={'ANSI X12 ' + retailerConfig?.edi?.version || "004010"} readOnly style={{ ...inputStyle, backgroundColor: '#f8fafc', color: '#64748b' }} />
                </div>
                <div>
                  <label style={labelStyle}>Supported Documents</label>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {Object.entries(retailerConfig?.edi?.documents || {}).map(([code, doc]) => (
                      <div key={code} style={{
                        padding: '12px 20px', borderRadius: '12px', border: '2px solid #10b981',
                        backgroundColor: 'rgba(16,185,129,0.1)', fontSize: '14px', fontWeight: '700', color: '#059669',
                        display: 'flex', alignItems: 'center', gap: '8px'
                      }}>
                        <Check size={16} strokeWidth={3} /> {code} ({doc.name})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Connection Details */}
          {currentStep === 3 && (selectedRetailer?.id === "custom" || retailerConfig) && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginBottom: '12px' }}>Connection Details</h2>
                <p style={{ fontSize: '16px', color: '#64748b' }}>Enter your SFTP credentials for {selectedRetailer.name}</p>
              </div>
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', marginBottom: '24px', fontSize: '14px', color: '#92400e' }}>
                  {retailerConfig?.sftp?.notes || "Enter your SFTP connection details below"}
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>SFTP Host</label>
                  <input type="text" value={formData.sftpHost} onChange={(e) => setFormData({...formData, sftpHost: e.target.value})}
                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                    onFocus={(e) => e.target.style.borderColor = '#10b981'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ ...fieldGroup, flex: 3 }}>
                    <label style={labelStyle}>Username</label>
                    <input type="text" value={formData.sftpUsername} onChange={(e) => setFormData({...formData, sftpUsername: e.target.value})}
                      placeholder="Your SFTP username" style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = '#10b981'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                  <div style={{ ...fieldGroup, flex: 2 }}>
                    <label style={labelStyle}>Port</label>
                    <input type="number" value={formData.sftpPort} onChange={(e) => setFormData({...formData, sftpPort: parseInt(e.target.value)})}
                      style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = '#10b981'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Password</label>
                  <input type="password" value={formData.sftpPassword} onChange={(e) => setFormData({...formData, sftpPassword: e.target.value})}
                    placeholder="Your SFTP password" style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#10b981'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review and Compliance */}
          {currentStep === 4 && (selectedRetailer?.id === "custom" || retailerConfig) && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginBottom: '12px' }}>Review and Confirm</h2>
                <p style={{ fontSize: '16px', color: '#64748b' }}>Verify your configuration for {selectedRetailer.name}</p>
              </div>
              <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                  <div style={{ padding: '20px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Retailer</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>{selectedRetailer.name}</div>
                  </div>
                  <div style={{ padding: '20px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Vendor Number</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', fontFamily: 'monospace' }}>{formData.vendorNumber || 'Not set'}</div>
                  </div>
                  <div style={{ padding: '20px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>SFTP Host</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', fontFamily: 'monospace' }}>{formData.sftpHost}</div>
                  </div>
                  <div style={{ padding: '20px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Protocol</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>{retailerConfig?.sftp?.protocol || "SFTP"}</div>
                  </div>
                </div>

                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Compliance Requirements</h3>
                <div style={{ padding: '20px', borderRadius: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '20px' }}>
                  {((retailerConfig?.onboarding?.requirements) || []).map((req, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', fontSize: '14px', color: '#166534' }}>
                      <Check size={16} strokeWidth={3} style={{ color: '#16a34a', flexShrink: 0 }} /> {req}
                    </div>
                  ))}
                </div>

                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Chargeback Rules</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: '#fef2f2', fontSize: '13px', color: '#991b1b' }}>
                    ASN must be sent within {retailerConfig?.edi?.asnRules.shipmentTimingHours} hours
                  </div>
                  <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: '#fef2f2', fontSize: '13px', color: '#991b1b' }}>
                    Invoice within {retailerConfig?.edi?.invoiceRules.invoiceTimingDays} days
                  </div>
                  <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: '#fef2f2', fontSize: '13px', color: '#991b1b' }}>
                    Label format: {retailerConfig?.edi?.asnRules.labelFormat}
                  </div>
                  <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: retailerConfig?.edi?.invoiceRules.allowPartialShipments ? '#f0fdf4' : '#fef2f2', fontSize: '13px', color: retailerConfig?.edi?.invoiceRules.allowPartialShipments ? '#166534' : '#991b1b' }}>
                    Partial shipments: {retailerConfig?.edi?.invoiceRules.allowPartialShipments ? 'Allowed' : 'Not allowed'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '120px', height: '120px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 32px', boxShadow: '0 12px 32px rgba(16,185,129,0.4)'
              }}>
                <PartyPopper size={60} style={{ color: 'white' }} />
              </div>
              <h2 style={{ fontSize: '42px', fontWeight: '900', color: '#0f172a', marginBottom: '16px' }}>All Set!</h2>
              <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '32px' }}>
                {selectedRetailer.name} has been successfully connected to your account
              </p>
              <div style={{ maxWidth: '600px', margin: '0 auto 40px', padding: '32px', borderRadius: '16px', background: 'rgba(16,185,129,0.1)', border: '2px solid rgba(16,185,129,0.3)' }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '20px' }}>What happens next:</div>
                {[
                  'EDI documents will be processed automatically every 15 minutes',
                  'Purchase orders from ' + selectedRetailer.name + ' will create draft orders in Shopify',
                  'Invoices and ship notices will be sent automatically when orders are fulfilled',
                  'All transactions will appear in your dashboard'
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '8px 0', textAlign: 'left' }}>
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <Check size={14} style={{ color: 'white' }} strokeWidth={3} />
                    </div>
                    <span style={{ fontSize: '15px', color: '#334155' }}>{item}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setPage('dashboard')} style={{
                background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none',
                borderRadius: '16px', padding: '20px 48px', fontSize: '18px', fontWeight: '800', cursor: 'pointer',
                boxShadow: '0 8px 16px rgba(16,185,129,0.3)', display: 'inline-flex', alignItems: 'center', gap: '12px'
              }}>
                <Check size={24} strokeWidth={3} /> Go to Dashboard
              </button>
            </div>
          )}

          {/* Navigation */}
          {currentStep < 5 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '48px', paddingTop: '32px', borderTop: '2px solid #e2e8f0' }}>
              <button onClick={prevStep} disabled={currentStep === 1} style={{
                backgroundColor: currentStep === 1 ? '#f1f5f9' : 'white',
                border: '2px solid #e2e8f0', color: currentStep === 1 ? '#94a3b8' : '#0f172a',
                padding: '16px 32px', borderRadius: '12px', fontSize: '16px', fontWeight: '700',
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <ChevronLeft size={20} /> Back
              </button>
              <button onClick={nextStep} disabled={(currentStep === 1 && !selectedRetailer) || saving} style={{
                background: (currentStep === 1 && !selectedRetailer) ? '#e2e8f0' : 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white', border: 'none', padding: '16px 32px', borderRadius: '12px',
                fontSize: '16px', fontWeight: '700',
                cursor: (currentStep === 1 && !selectedRetailer) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
                boxShadow: (currentStep === 1 && !selectedRetailer) ? 'none' : '0 4px 12px rgba(16,185,129,0.3)',
                opacity: saving ? 0.7 : 1
              }}>
                {saving ? 'Saving...' : currentStep === 4 ? 'Complete Setup' : 'Next Step'}
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PartnerSetupWizard;
